import React, { useMemo } from 'react';
import { Box, Checkbox, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from '../i18n/useTranslation';

export type PrivilegeKey = string; // format: `${resource}:${action}`

export interface PrivilegesMatrixProps {
  value: PrivilegeKey[];
  onChange?: (next: PrivilegeKey[]) => void;
  resources?: string[];
  actions?: string[]; // default: ['create','read','update','delete']
  readOnly?: boolean;
}

const defaultResources = [
  'applications',
  'reports',
  'rules',
  'users',
  'merchants',
  'transactions',
  'settings',
  'teams'
];

const defaultActions = ['create', 'delete', 'read', 'update'];

const PrivilegesMatrix: React.FC<PrivilegesMatrixProps> = ({ value, onChange, resources = defaultResources, actions = defaultActions, readOnly = false }) => {
  const { t } = useTranslation();

  const setChecked = (resource: string, action: string, checked: boolean) => {
    const key = `${resource}:${action}`;
    if (!onChange) return;
    if (checked) {
      if (!value.includes(key)) onChange([...value, key]);
    } else {
      onChange(value.filter(v => v !== key));
    }
  };

  const isChecked = (resource: string, action: string) => value.includes(`${resource}:${action}`);

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: `200px repeat(${actions.length}, 140px)`, gap: 1, alignItems: 'center' }}>
        <Box />
        {actions.map(a => (
          <Box key={a} sx={{ py: 1 }}>
            <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>{t(`priv.action.${a}`)}</Typography>
          </Box>
        ))}
        <Divider sx={{ gridColumn: `1 / span ${actions.length + 1}`, my: 0.5 }} />
        {resources.map(r => (
          <React.Fragment key={r}>
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" fontWeight={700}>{t(`priv.resource.${r}`)}</Typography>
            </Box>
            {actions.map(a => (
              <Box key={`${r}-${a}`} sx={{ py: 1 }}>
                <Checkbox
                  checked={isChecked(r, a)}
                  onChange={(e) => setChecked(r, a, e.target.checked)}
                  color={a === 'read' ? 'secondary' : 'primary'}
                  disabled={!!readOnly}
                />
              </Box>
            ))}
            <Divider sx={{ gridColumn: `1 / span ${actions.length + 1}`, my: 0.5 }} />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default PrivilegesMatrix;


